<template>
  <v-card class="brief-form-card">
    <v-card-title
      class="pb-0"
      primary-title
    >
      Informacje podstawowe
    </v-card-title>

    <v-card-text class="pb-0">
      <v-row>
        <v-col
          cols="12"
          md="6"
          lg="3"
        >
          <label class="input-label">Nazwa projektu *</label>
          <v-text-field
            :value="value.name"
            v-bind="props.textField"
            :rules="[rules.required, rules.maxLength(100)]"
            height="40"
            placeholder="Podaj nazwę"
            :disabled="isNotEditable"
            @input="update('name', $event)"
          />
        </v-col>
        <v-col
          cols="12"
          md="6"
          lg="3"
        >
          <label class="input-label">Typ projektu *</label>
          <v-select
            :value="value.projectType"
            :items="filtersOptions.projectTypes"
            :rules="[rules.required]"
            placeholder="Wybierz typ"
            v-bind="props.select"
            :hide-details="false"
            height="40"
            :disabled="isNotEditable"
            @input="update('projectType', $event)"
          />
        </v-col>
        <v-col
          cols="12"
          md="6"
          lg="3"
        >
          <label class="input-label">Media *</label>
          <v-select
            :value="value.mediumIds"
            :items="filtersOptions.media"
            :rules="[rules.required]"
            placeholder="Wybierz media"
            v-bind="props.select"
            multiselect
            item-text="name"
            item-value="id"
            multiple
            :hide-details="false"
            height="40"
            :disabled="isNotEditable"
            @input="update('mediumIds', $event)"
          >
            <template #selection="{ item, index }">
              <AppMultiselectChoices
                :visible-items="3"
                :index="index"
                :array-length="value.mediumIds.length"
                :label="item.name"
              />
            </template>
          </v-select>
        </v-col>
        <v-col
          cols="12"
          md="6"
          lg="3"
        >
          <label class="input-label">Termin wykonania *</label>
          <v-menu
            ref="datePicker"
            v-model="isDatePickerShown"
            v-bind="props.datePickerMenu"
            :disabled="isNotEditable"
          >
            <template #activator="{ on, attrs }">
              <div v-on="on">
                <v-text-field
                  :value="value.deadline | displayDate"
                  :rules="[rules.required]"
                  persistent-hint
                  v-bind="{ ...attrs, ...props.shortTextField }"
                  height="40"
                  readonly
                  placeholder="Wybierz datę"
                  class="input-calendar"
                  append-icon="mdi-calendar-month-outline"
                  :disabled="isNotEditable"
                  @input="update('deadline', $event)"
                />
              </div>
            </template>
            <v-date-picker
              v-model="value.deadline"
              :allowed-dates="allowedDates"
              v-bind="props.datePicker"
              :disabled="isNotEditable"
              @input="isDatePickerShown = false"
            />
          </v-menu>
        </v-col>
      </v-row>
      <v-row class="mt-0">
        <v-col
          class="pt-0"
          cols="12"
          md="6"
          lg="3"
        >
          <label class="input-label">Lokalizacja projektu</label>
          <v-text-field
            :value="value.discLocation"
            v-bind="props.textField"
            :disabled="isNotCreateProjectRoute && !value.locationEditable"
            height="40"
            validate-on-blur
            placeholder="Podaj lokalizację"
            @input="update('discLocation', $event)"
          />
        </v-col>
        <v-col
          class="pt-0"
          cols="12"
          md="6"
          lg="3"
        >
          <label
            class="input-label"
          >Youtube URL
            <v-btn
              v-if="isNotCreateProjectRoute"
              icon
              x-small
              title="Ustaw miniaturkę"
              :disabled="
                isNotEditable ||
                  !value.youtubeLink ||
                  setYoutubeThumbnailValue === value.youtubeThumbnailUrl
              "
              @click="setYoutubeThumbnail"
            ><v-icon small>mdi-account-box-outline</v-icon></v-btn>
          </label>
          <v-text-field
            :value="value.youtubeLink"
            v-bind="props.textField"
            height="40"
            validate-on-blur
            placeholder="Podaj url youtube"
            :disabled="isNotEditable"
            @input="update('youtubeLink', $event)"
          />
        </v-col>
        <v-col
          cols="12"
          md="6"
          lg="3"
          class="pt-0"
        >
          <label class="input-label">Link</label>
          <v-text-field
            :value="value.hyperlink"
            v-bind="props.textField"
            :rules="value.hyperlink ? [rules.url] : []"
            height="40"
            placeholder="Podaj zewnętrzny link"
            :disabled="isNotEditable"
            @input="update('hyperlink', $event)"
          />
        </v-col>
        <v-col
          v-if="setPriorityPermitted"
          cols="12"
          md="6"
          lg="3"
          class="pt-0"
        >
          <label class="input-label">Priorytet</label>
          <v-select
            :value="value.priority"
            :items="filtersOptions.projectPriorities"
            placeholder="Wybierz priorytet"
            v-bind="props.select"
            :hide-details="false"
            height="40"
            validate-on-blur
            :disabled="isNotEditable"
            @input="update('priority', $event)"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="12"
          md="6"
          lg="3"
          class="pt-0 d-flex align-center mt-n5"
        >
          <v-checkbox
            dense
            hide-details
            class="mt-0"
            :disabled="isNotCreateProjectRoute && !value.printCoordinationEditable"
            :input-value="value.printCoordination"
            @change="update('printCoordination', $event)"
          /><label class="text--body-2">Do druku</label>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import AppMultiselectChoices from '@/components/UI/AppMultiselectChoices.vue';
import props from '@/helpers/componentsProps';
import rules from '@/helpers/validators';
import utils from '@/helpers/utils';
import { mapState, mapGetters, mapActions } from 'vuex';
import axios from 'axios';
import debounce from 'lodash.debounce';

export default {
  props: {
    value: {
      type: Object,
      required: true,
    },
  },

  components: {
    AppMultiselectChoices,
  },

  data() {
    return {
      props,
      rules,
      isDatePickerShown: false,
      setYoutubeThumbnailValue: '',
    };
  },

  computed: {
    ...mapState('filters', ['filtersOptions']),

    ...mapGetters('auth', ['checkUserPermissions']),

    setPriorityPermitted() {
      const availability = ['admin_director', 'chief'];
      return this.checkUserPermissions(availability);
    },

    isNotCreateProjectRoute() {
      return this.$route.name !== 'create-project';
    },

    isNotEditable() {
      // eslint-disable-next-line no-prototype-builtins
      return this.value?.hasOwnProperty('editable') && this.value?.editable === false;
    },
  },

  methods: {
    ...mapActions('snackbar', ['showSnackbar']),

    update(key, value) {
      this.$emit('input', { ...this.value, [key]: value });
    },

    allowedDates(val) {
      return utils.allowedDates(val, this.value.createdAt);
    },
    setYoutubeThumbnail: debounce(
      async function () {
        try {
          const videoUrl = this.value.youtubeLink;
          const requestUrl = `https://youtube.com/oembed?url=${videoUrl}&format=json`;
          const { data } = await axios.get(requestUrl);
          this.update('youtubeThumbnailUrl', data.thumbnail_url);
          this.setYoutubeThumbnailValue = data.thumbnail_url;
          this.showSnackbar({
            message: ['Wybrano miniaturę'],
            type: 'success',
          });
        } catch (e) {
          this.showSnackbar({
            message: ['Nie można pobrać miniatury'],
            type: 'error',
          });
        }
      },
      1000,
      { leading: true },
    ),
  },
};
</script>
